import { useState, useEffect, useRef } from 'react';
import { Label } from './label';
import { cn } from '@/lib/utils';
import Select from 'react-select';

// Define the format for each location option
interface LocationOption {
  label: string;
  value: string;
  placeId?: string;
  place?: {
    geometry?: {
      location?: {
        lat: number;
        lng: number;
      };
    };
  } | null;
  countryCode?: string;
  currency?: string;
}

interface GooglePlacesInputProps {
  name: string;
  value: LocationOption[] | LocationOption;
  labelName: string;
  placeholder?: string;
  errorLabel?: string;
  requiredStar?: boolean;
  fieldName?: string;
  index?: number;
  onChange: (e: { target: { name: string; value: LocationOption[] | LocationOption } }, fieldName?: string, index?: number) => void;
  debug?: boolean;
  isMulti?: boolean;
}

export function GooglePlacesInput({
  name,
  value,
  labelName,
  placeholder,
  errorLabel,
  requiredStar = false,
  fieldName = 'property_name',
  index = 0,
  onChange,
  debug = false,
  isMulti = true,
}: GooglePlacesInputProps) {
  // Set default placeholder based on isMulti mode
  placeholder = placeholder || (isMulti ? 'Search and select multiple locations' : 'Search for a location...');
  
  // Ensure value is properly initialized for the select component
  const selectValue = isMulti 
    ? Array.isArray(value) ? value : []
    : Array.isArray(value) ? value[0] || null : value;
  const [options, setOptions] = useState<LocationOption[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(false);
  
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);

  // Country code to currency mapping
  const countryCurrencyMap: Record<string, string> = {
    'US': 'USD', 'IN': 'INR', 'GB': 'GBP', 'CA': 'CAD', 'AU': 'AUD', 'JP': 'JPY',
    'DE': 'EUR', 'FR': 'EUR', 'IT': 'EUR', 'ES': 'EUR', 'NL': 'EUR', 'BE': 'EUR',
    'AT': 'EUR', 'PT': 'EUR', 'IE': 'EUR', 'GR': 'EUR', 'FI': 'EUR', 'LU': 'EUR',
    'CN': 'CNY', 'KR': 'KRW', 'SG': 'SGD', 'HK': 'HKD', 'TW': 'TWD', 'TH': 'THB',
    'MY': 'MYR', 'ID': 'IDR', 'PH': 'PHP', 'VN': 'VND', 'BD': 'BDT', 'PK': 'PKR',
    'LK': 'LKR', 'NP': 'NPR', 'BT': 'BTN', 'MV': 'MVR', 'AE': 'AED', 'SA': 'SAR',
    'QA': 'QAR', 'KW': 'KWD', 'BH': 'BHD', 'OM': 'OMR', 'JO': 'JOD', 'LB': 'LBP',
    'TR': 'TRY', 'EG': 'EGP', 'ZA': 'ZAR', 'NG': 'NGN', 'KE': 'KES', 'GH': 'GHS',
    'BR': 'BRL', 'MX': 'MXN', 'AR': 'ARS', 'CL': 'CLP', 'CO': 'COP', 'PE': 'PEN',
    'RU': 'RUB', 'PL': 'PLN', 'CZ': 'CZK', 'HU': 'HUF', 'RO': 'RON', 'BG': 'BGN',
    'HR': 'HRK', 'RS': 'RSD', 'BA': 'BAM', 'MK': 'MKD', 'AL': 'ALL', 'ME': 'EUR',
    'CH': 'CHF', 'NO': 'NOK', 'SE': 'SEK', 'DK': 'DKK', 'IS': 'ISK', 'NZ': 'NZD'
  };

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 10;

    const initializeGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        try {
          autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
          placesServiceRef.current = new window.google.maps.places.PlacesService(
            document.createElement('div')
          );
          setIsGoogleMapsReady(true);
          if (debug) console.log('Google Maps services initialized successfully');
          return;
        } catch (error) {
          console.error('Error initializing Google Maps services:', error);
        }
      }

      // If Google Maps is not ready and we haven't exceeded max retries
      if (retryCount < maxRetries) {
        retryCount++;
        setTimeout(initializeGoogleMaps, 500);
      } else {
        console.error('Failed to initialize Google Maps after maximum retries');
        setIsGoogleMapsReady(false);
      }
    };

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      initializeGoogleMaps();
    } else {
      // Listen for the Google Maps script to load
      const checkGoogleMaps = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          initializeGoogleMaps();
        } else {
          setTimeout(checkGoogleMaps, 100);
        }
      };
      checkGoogleMaps();
    }

    // Cleanup function
    return () => {
      setIsGoogleMapsReady(false);
    };
  }, [debug]);

  const extractCountryInfo = (place: google.maps.places.PlaceResult): { countryCode: string; currency: string } => {
    let countryCode = 'US'; // Default fallback
    let currency = 'USD'; // Default fallback

    if (place.address_components) {
      const countryComponent = place.address_components.find(
        component => component.types.includes('country')
      );

      if (countryComponent) {
        countryCode = countryComponent.short_name;
        currency = countryCurrencyMap[countryCode] || 'USD';
      }
    }

    return { countryCode, currency };
  };

  const getPlaceDetails = (placeId: string): Promise<google.maps.places.PlaceResult> => {
    return new Promise((resolve, reject) => {
      if (!placesServiceRef.current) {
        reject('Places service not available');
        return;
      }

      if (!placeId) {
        reject('Missing placeId parameter');
        return;
      }

      placesServiceRef.current.getDetails(
        {
          placeId: placeId,
          fields: ['name', 'formatted_address', 'geometry', 'place_id', 'types', 'address_components']
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            resolve(place);
          } else {
            reject(`Places service error: ${status}`);
          }
        }
      );
    });
  };

  const handleInputChange = (inputValue: string) => {
    setInputValue(inputValue);

    if (!inputValue || !autocompleteServiceRef.current) {
      if (debug) console.log('No input value or autocomplete service not available');
      setOptions([]);
      return;
    }

    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: inputValue,
        types: ['lodging'],
      },
      (predictions, status) => {
        if (debug) console.log('Predictions status:', status, predictions);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const newOptions = predictions.map((prediction) => ({
            label: prediction.description,
            value: prediction.description,
            placeId: prediction.place_id,
          }));
          setOptions(newOptions);
        } else {
          setOptions([]);
        }
      }
    );
  };

  const handleSelectionChange = async (selectedOptions: readonly LocationOption[] | LocationOption | null) => {
    if (!selectedOptions) {
      // Return empty array or null based on isMulti mode
      onChange({
        target: {
          name,
          value: isMulti ? [] : null,
        },
      }, fieldName, index);
      return;
    }

    // Handle both multi-select and single-select modes
    const options = Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions];
    const formattedLocations: LocationOption[] = [];

    for (const option of options) {
      try {
        // Check if this option is already formatted (existing selection)
        if (option.value && option.value.includes('@') && option.countryCode && option.currency) {
          // This is already a formatted location, preserve it as-is
          formattedLocations.push({
            label: option.label,
            value: option.value,
            place: option.place,
            countryCode: option.countryCode,
            currency: option.currency,
          });
          continue;
        }

        // Check if placeId exists before making the API call
        if (!option.placeId) {
          if (debug) console.warn('No placeId found for option:', option);
          // Fallback to basic format without place details
          const fallbackLocationString = `${option.label}@US@USD`;
          formattedLocations.push({
            label: option.label,
            value: fallbackLocationString,
            place: null,
            countryCode: 'US',
            currency: 'USD',
          });
          continue;
        }

        const place = await getPlaceDetails(option.placeId);
        const formattedName = `${place?.name}, ${place?.formatted_address}` || option.label || place?.name || '';
        const { countryCode, currency } = extractCountryInfo(place);
        const locationString = `${formattedName}@${countryCode}@${currency}`;

        formattedLocations.push({
          label: formattedName,
          value: locationString,
          place: place?.geometry ? {
            geometry: {
              location: {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
              }
            }
          } : null,
          countryCode,
          currency,
        });
      } catch (error) {
        console.error('Error getting place details:', error);
        // Fallback to basic format if place details fail
        const fallbackLocationString = `${option.label}@US@USD`;
        formattedLocations.push({
          label: option.label,
          value: fallbackLocationString,
          place: null,
          countryCode: 'US',
          currency: 'USD',
        });
      }
    }

    // Return array or single object based on isMulti mode
    onChange({
      target: {
        name,
        value: isMulti ? formattedLocations : formattedLocations[0] || null,
      },
    }, fieldName, index);
  };

  if (!isGoogleMapsReady) {
    return (
      <div className="space-y-2">
        {labelName && (
          <Label htmlFor={name}>
            {labelName} {requiredStar && <span className="text-destructive">*</span>}
          </Label>
        )}
        <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm">
          Loading Google Maps...
        </div>
        {errorLabel && (
          <p className="text-xs text-destructive">{errorLabel}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {labelName && (
        <Label htmlFor={name}>
          {labelName} {requiredStar && <span className="text-destructive">*</span>}
        </Label>
      )}
      <Select
        value={selectValue}
        options={options}
        name={name}
        placeholder={placeholder}
        isMulti={isMulti}
        isSearchable={true}
        isClearable={true}
        onChange={handleSelectionChange}
        onInputChange={handleInputChange}
        filterOption={() => true} // Disable client-side filtering to let Google handle it
        className="basic-select"
        classNamePrefix="react-select"
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            minHeight: '40px',
            borderColor: errorLabel ? 'var(--destructive)' : baseStyles.borderColor,
          }),
        }}
      />
      {errorLabel && (
        <p className="text-xs text-destructive">{errorLabel}</p>
      )}
    </div>
  );
}
